import React, { useState, useEffect, useCallback, useRef } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  subscribeToBooking, startEnRoute, completeTrip, updateDriverLocation,
  markStopComplete, markStopUnreachable, acceptStopChange, declineStopChange, recordTripDistance,
} from '../../services/tripService';
import { verifyTripPin, markNoShow, cancelBooking } from '../../services/bookingService';
import { useGeofenceDetection } from '../../hooks/useGeofenceDetection';
import { useWaitingTimer } from '../../hooks/useWaitingTimer';
import { useTripMonitoring } from '../../hooks/useTripMonitoring';
import { TripPinEntry, GeofenceIndicator, WaitingTimerDisplay, RouteMap, NavigateButton } from '../../components/booking/BookingComponents';
import { SOSButton } from '../../components/safety/SafetyComponents';
import ChatWindow from '../../components/messaging/ChatWindow';
import { triggerSOS } from '../../services/safetyService';
import { logGeofenceEvent } from '../../services/tripService';
import toast from 'react-hot-toast';

export default function TripManagement() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [booking, setBooking] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [pinLocked, setPinLocked] = useState(false);
  const [pinAttemptsRemaining, setPinAttemptsRemaining] = useState(3);
const [pinLockoutMinutes, setPinLockoutMinutes] = useState(0);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  // Multi-stop (Batch 11)
  const [showAllStops, setShowAllStops] = useState(false);
  const [stopActionInProgress, setStopActionInProgress] = useState(null); // stopIndex being acted on
  const [unreachableStopIdx, setUnreachableStopIdx] = useState(null);
  const [unreachableReason, setUnreachableReason] = useState('');
  const [changeActionInProgress, setChangeActionInProgress] = useState(null);


  useEffect(() => {
    if (!bookingId) return;
    const unsub = subscribeToBooking(bookingId, setBooking);
    return unsub;
  }, [bookingId]);

  // Auto-navigate to rating page once trip completes (with 2.5s grace)
  useEffect(() => {
    if (booking?.status === 'completed' && !booking.driverRated) {
      const t = setTimeout(() => navigate(`/driver/rate/${bookingId}`), 2500);
      return () => clearTimeout(t);
    }
  }, [booking?.status, booking?.driverRated, bookingId, navigate]);


  const pickupLat = booking?.pickup?.lat;
  const pickupLng = booking?.pickup?.lng;
  const isEnRouteOrLater = ['en_route', 'arrived', 'pin_verified', 'in_progress'].includes(booking?.status);

  // ─── GPS metering during in_progress (geofence watch is disabled in this phase) ───
  const meterPosRef = useRef(null);
  const lastSentRef = useRef(0);
  const [showDeviationModal, setShowDeviationModal] = useState(false);
  const [deviationReason, setDeviationReason] = useState('');
  useEffect(() => {
    if (booking?.status !== 'in_progress' || !bookingId || !navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        meterPosRef.current = { lat, lng };
        const now = Date.now();
        if (now - lastSentRef.current >= 8000) {   // throttle: ~every 8s
          lastSentRef.current = now;
          recordTripDistance(bookingId, lat, lng).catch(() => {});
          updateDriverLocation(bookingId, lat, lng).catch(() => {});  // live position for rider's map
        }
      },
      (err) => console.error('Metering GPS error:', err),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [booking?.status, bookingId]);

  const { isInsideGeofence, hasArrived, distanceToPickup, currentPosition, setCallbacks } = useGeofenceDetection(
    pickupLat, pickupLng, isEnRouteOrLater && booking?.status !== 'in_progress'
  );

  const { totalSeconds, waitingFee, isRunning, isPaused, isInFreeWindow, formatTime, formatFreeRemaining } = useWaitingTimer(
    isInsideGeofence, hasArrived
  );

  const { durationOverrun, checkInDue, acknowledgeCheckIn } = useTripMonitoring(booking, currentPosition, booking?.status === 'in_progress');

  // Set geofence callbacks
  useEffect(() => {
    setCallbacks({
      onArrive: () => {
        logGeofenceEvent(bookingId, { type: 'entered', timestamp: new Date().toISOString() });
        toast.success('Arrived at pickup — waiting timer started');
      },
      onLeave: () => {
        logGeofenceEvent(bookingId, { type: 'exited', timestamp: new Date().toISOString() });
        toast('Left pickup zone — timer paused', { icon: '⏸️' });
      },
      onReturn: () => {
        logGeofenceEvent(bookingId, { type: 're-entered', timestamp: new Date().toISOString() });
        toast('Back at pickup — timer resumed', { icon: '▶️' });
      },
    });
  }, [bookingId, setCallbacks]);

  // Update driver location periodically
  useEffect(() => {
    if (!currentPosition || !bookingId || !isEnRouteOrLater) return;
    updateDriverLocation(bookingId, currentPosition.lat, currentPosition.lng);
  }, [currentPosition, bookingId, isEnRouteOrLater]);

  const handleStartEnRoute = async () => {
    try { await startEnRoute(bookingId); toast.success('Marked as en route'); } catch (err) { toast.error(err.message); }
  };

  const handlePinSubmit = async (pin) => {
    try {
      const result = await verifyTripPin(bookingId, pin);
      if (result.verified) {
        toast.success('PIN verified — trip started!');
      } else if (result.locked) {
        setPinLocked(true);
        setPinLockoutMinutes(result.lockoutMinutes || 5);
        toast.error(`Too many attempts. Locked for ${result.lockoutMinutes} minutes.`);
        setTimeout(() => { setPinLocked(false); setPinAttemptsRemaining(3); }, (result.lockoutMinutes || 5) * 60000);
      } else {
        setPinAttemptsRemaining(result.attemptsRemaining || 0);
        toast.error(`Wrong PIN. ${result.attemptsRemaining} attempt${result.attemptsRemaining !== 1 ? 's' : ''} left.`);
      }
    } catch (err) { toast.error('Verification failed'); }
  };

  const handleCompleteTrip = async (reason) => {
    const finalPos = meterPosRef.current || currentPosition;
    if (!finalPos) { toast.error('Unable to get location'); return; }
    // If the actual distance is notably longer than planned and no reason was given yet,
    // ask the driver why before completing (so a genuine detour can be reviewed/compensated).
    const estKm = booking?.estimatedDistance || 0;
    const localActualKm = (booking?.actualDistanceMeters || 0) / 1000;
    if (!reason && estKm > 0 && localActualKm > estKm * 1.15) {
      setShowDeviationModal(true);
      return;
    }
    try {
      try { await recordTripDistance(bookingId, finalPos.lat, finalPos.lng); } catch (e) {}
      await completeTrip(bookingId, finalPos, reason || null);
      toast.success('Trip completed!');
      setShowDeviationModal(false);
    } catch (err) { toast.error(err.message); }
  };

  const handleNoShow = async () => {
    if (!confirm('Mark rider as no-show? They will be charged the full fare.')) return;
    try { await markNoShow(bookingId); toast.success('No-show recorded'); } catch (err) { toast.error(err.message); }
  };

  const handleDriverCancel = async () => {
    if (!cancelReason) { toast.error('Please select a reason'); return; }
    setCancelling(true);
    try {
      const result = await cancelBooking(bookingId, cancelReason);
      if (result.returnedToPool) {
        toast.success(`Booking returned to pool. ${result.driverPenalty > 0 ? `$${result.driverPenalty} penalty applied.` : 'No penalty.'}`);
      } else {
        toast.success('Booking cancelled');
      }
      setShowCancelModal(false);
      navigate('/driver');
    } catch (err) {
      toast.error(err.message || 'Cancellation failed');
    }
    setCancelling(false);
  };


// ─── Multi-stop handlers (Batch 11) ───
  const handleMarkStopComplete = async (stopIndex) => {
    if (!currentPosition) { toast.error('Unable to get location'); return; }
    setStopActionInProgress(stopIndex);
    try {
      const result = await markStopComplete(bookingId, stopIndex, currentPosition);
      if (result.gpsVerified) {
        toast.success(`Stop ${stopIndex + 1} completed`);
      } else {
        toast.success(`Stop ${stopIndex + 1} completed (GPS verification: ${result.distanceFromStop}m away — flagged)`);
      }
    } catch (err) {
      toast.error(err.message || 'Could not mark stop complete');
    }
    setStopActionInProgress(null);
  };

  const handleMarkStopUnreachable = async () => {
    if (unreachableStopIdx == null || !unreachableReason) return;
    setStopActionInProgress(unreachableStopIdx);
    try {
      const result = await markStopUnreachable(bookingId, unreachableStopIdx, unreachableReason);
      if (result.stopNoShowFee > 0) {
        toast.success(`Stop logged unreachable. $${result.stopNoShowFee} no-show fee added.`);
      } else {
        toast.success('Stop logged unreachable');
      }
      setUnreachableStopIdx(null);
      setUnreachableReason('');
    } catch (err) {
      toast.error(err.message || 'Could not log unreachable stop');
    }
    setStopActionInProgress(null);
  };

  const handleAcceptChange = async (changeId) => {
    setChangeActionInProgress(changeId);
    try {
      await acceptStopChange(bookingId, changeId);
      toast.success('Stop change accepted — fare updated');
    } catch (err) {
      toast.error(err.message || 'Could not accept change');
    }
    setChangeActionInProgress(null);
  };

  const handleDeclineChange = async (changeId) => {
    setChangeActionInProgress(changeId);
    try {
      await declineStopChange(bookingId, changeId, '');
      toast.success('Stop change declined');
    } catch (err) {
      toast.error(err.message || 'Could not decline change');
    }
    setChangeActionInProgress(null);
  };

  const handleSOS = async () => {
    if (!currentPosition) return;
    try {
      await triggerSOS(bookingId, userProfile.id, 'driver', currentPosition);
      toast.success('SOS activated');
    } catch (err) { toast.error('SOS failed'); }
  };

  const handleCheckIn = () => {
    acknowledgeCheckIn();
    toast.success('Check-in confirmed');
  };

  if (!booking) return <div className="p-10 text-center">Loading...</div>;

  // Determine nav target: pickup if heading to pickup, destination if in_progress
  const navTarget = booking.status === 'in_progress' ? booking.destination : booking.pickup;
  const navLabel = booking.status === 'in_progress' ? 'Navigate to Destination' : 'Navigate to Pickup';

  return (
    <div className="max-w-[600px] mx-auto p-6">
      {/* Back to Dashboard */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/driver')}
          className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0"
        >
          ← Dashboard
        </button>
      </div>

      <h1 className="font-serif mt-0 mb-1">Trip: {booking.referenceNumber}</h1>
      <div className="text-[0.85rem] text-muted mb-2">Status: {booking.status}</div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {booking.seriesId && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.75rem] font-bold" style={{ background: 'rgba(99,102,241,0.08)', color: '#4f46e5' }}>
            🔁 Recurring · {booking.seriesReferenceNumber}
            {typeof booking.occurrenceIndex === 'number' && <span className="text-muted">occurrence #{booking.occurrenceIndex + 1}</span>}
          </div>
        )}
        {booking.vehicleGroupId && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.75rem] font-bold" style={{ background: 'rgba(139,92,246,0.08)', color: '#7c3aed' }}>
            🚐 Multi-vehicle · {booking.vehicleGroupReferenceNumber}
            {typeof booking.vehicleIndex === 'number' && <span className="text-muted">vehicle #{booking.vehicleIndex + 1}</span>}
          </div>
        )}
        {booking.standbyCompensation > 0 && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.75rem] font-bold" style={{ background: 'rgba(245,158,11,0.1)', color: '#92400e' }}>
            💰 Standby comp · ${booking.standbyCompensation.toFixed(2)}
          </div>
        )}
      </div>



      {/* Route Map */}
      {booking.pickup && booking.destination && (
        <div className="mb-4">
          <RouteMap
            pickup={booking.pickup}
            destination={booking.destination}
            routePolyline={booking.routePolyline}
            height={240}
          />
        </div>
      )}

      {/* Navigate button — show during active trip */}
      {['en_route', 'arrived', 'pin_verified', 'in_progress'].includes(booking.status) && navTarget?.lat && (
        <div className="mb-4">
          <NavigateButton destination={navTarget} label={navLabel} />
        </div>
      )}

      {/* Check-in Alert */}
      {checkInDue && (
        <div className="p-4 rounded-xl mb-4 text-center" style={{ background: '#fef3cd', border: '1px solid #fbbf24' }}>
          <div className="font-bold mb-2" style={{ color: '#92400e' }}>Driver check-in required</div>
          <button onClick={handleCheckIn} className="btn btn-primary" style={{ padding: '10px 24px' }}>I'm OK</button>
        </div>
      )}

      {/* Duration Overrun Warning */}
      {durationOverrun > 15 && (
        <div className="p-3 rounded-xl mb-4 text-center font-semibold" style={{ background: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.2)', color: '#ff6b6b' }}>
          Trip is {durationOverrun} min over estimated duration
        </div>
      )}

      {/* Route Info */}
      <div className="p-4 rounded-2xl border border-slate-200 mb-4">
        <div className="text-[0.85rem] text-body">
          <strong>Pickup:</strong> {booking.pickupAddress}<br />
          <strong>Destination:</strong> {booking.destinationAddress}<br />
          <strong>Rider:</strong> {booking.riderName || 'Rider'} · {booking.passengerCount} pax
          {booking.hasMultiStop && (
            <><br /><strong>Stops:</strong> {booking.stops.filter(s => s.status === 'completed').length}/{booking.stops.length} complete</>
          )}
        </div>
      </div>

      {/* Vulnerable-passenger flags (Batch 10) — prominent for driver awareness */}
      {(booking.hasSupportAnimal || booking.mobilityAidRequired || booking.cognitiveSupport ||
        booking.accessibilityNeeded || booking.hasMinor || booking.childSeatRequired) && (
        <div className="p-3.5 rounded-2xl mb-4" style={{ background: 'rgba(99,102,241,0.06)', border: '1.5px solid rgba(99,102,241,0.2)' }}>
          <div className="text-[0.78rem] font-bold uppercase mb-1.5" style={{ color: '#4f46e5' }}>
            🛡 Passenger support requirements
          </div>
          <div className="flex flex-wrap gap-1.5">
            {booking.hasSupportAnimal && (
              <span className="px-2.5 py-1 rounded-full bg-white text-[0.78rem] font-semibold">
                🐕 Guide/support animal{booking.supportAnimalDetails && `: ${booking.supportAnimalDetails}`}
              </span>
            )}
            {booking.mobilityAidRequired && (
              <span className="px-2.5 py-1 rounded-full bg-white text-[0.78rem] font-semibold">
                ♿ Mobility aid
              </span>
            )}
            {booking.accessibilityNeeded && !booking.mobilityAidRequired && (
              <span className="px-2.5 py-1 rounded-full bg-white text-[0.78rem] font-semibold">
                ♿ Accessibility needs
              </span>
            )}
            {booking.cognitiveSupport && (
              <span className="px-2.5 py-1 rounded-full bg-white text-[0.78rem] font-semibold">
                🧠 Cognitive support
              </span>
            )}
            {booking.hasMinor && (
              <span className="px-2.5 py-1 rounded-full bg-white text-[0.78rem] font-semibold">
                👶 Minor passenger
              </span>
            )}
            {booking.childSeatRequired && (
              <span className="px-2.5 py-1 rounded-full bg-white text-[0.78rem] font-semibold">
                🪑 Child seat
              </span>
            )}
          </div>
          {booking.emergencyContactPhone && (
            <div className="mt-2 text-[0.78rem] text-body">
              <strong>Emergency contact for this trip:</strong> {booking.emergencyContactName} · {booking.emergencyContactPhone}
            </div>
          )}
        </div>
      )}


      {/* Multi-stop pending change banner (Batch 11) */}
      {booking.hasPendingStopChange && (booking.pendingStopChanges || []).map((change) => {
        const expiresAt = change.expiresAt?.toDate ? change.expiresAt.toDate() : new Date(change.expiresAt);
        const secondsLeft = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
        const fareDelta = (change.proposedFareBreakdown?.totalFare || 0) - (booking.fareBreakdown?.totalFare || 0);
        return (
          <div key={change.changeId} className="p-4 rounded-2xl mb-4"
            style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '2px solid #f59e0b' }}>
            <div className="font-extrabold mb-1.5" style={{ color: '#92400e' }}>
              ⏱ Rider requested a stop change ({secondsLeft}s left)
            </div>
            <div className="text-[0.85rem] mb-3" style={{ color: '#78350f' }}>
              Change type: <strong>{change.changeType}</strong><br />
              New total fare: <strong>${change.proposedFareBreakdown?.totalFare?.toFixed(2)}</strong>
              {' '}({fareDelta >= 0 ? '+' : ''}${fareDelta.toFixed(2)})
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleDeclineChange(change.changeId)}
                disabled={changeActionInProgress === change.changeId}
                className="flex-1 px-3.5 py-2.5 rounded-[10px] bg-white cursor-pointer font-semibold"
                style={{ border: '1.5px solid #92400e', color: '#92400e' }}
              >Decline</button>
              <button
                onClick={() => handleAcceptChange(change.changeId)}
                disabled={changeActionInProgress === change.changeId}
                className="flex-1 px-3.5 py-2.5 rounded-[10px] border-0 text-white cursor-pointer font-bold"
                style={{ background: '#059669' }}
              >Accept</button>
            </div>
          </div>
        );
      })}

      {/* Multi-stop list (Batch 11) — shown during in_progress */}
      {booking.hasMultiStop && booking.status === 'in_progress' && (() => {
        const stops = booking.stops || [];
        const nextStopIdx = stops.findIndex(s => s.status === 'pending');
        const nextStop = nextStopIdx >= 0 ? stops[nextStopIdx] : null;
        return (
          <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
            <div className="flex justify-between items-center mb-3">
              <span className="font-bold text-brand-dark">Trip Stops</span>
              <button onClick={() => setShowAllStops(!showAllStops)} className="px-2.5 py-1 rounded-md border border-slate-200 bg-white cursor-pointer text-[0.78rem] font-semibold">{showAllStops ? 'Hide all' : 'Show all'}</button>
            </div>

            {/* Next stop prominent */}
            {nextStop && (
              <div className="p-3.5 rounded-xl mb-2.5" style={{ background: '#f0fdf4', border: '2px solid #2dd4a8' }}>
                <div className="text-[0.75rem] font-bold uppercase mb-1" style={{ color: '#059669' }}>
                  Next stop ({nextStopIdx + 1} of {stops.length})
                </div>
                <div className="font-bold text-brand-dark mb-1">
                  {nextStop.type === 'pickup' ? `🧍 Pickup — ${nextStop.coRiderName}` : '👋 Drop-off'}
                </div>
                <div className="text-[0.85rem] text-body mb-2.5">
                  {nextStop.address}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleMarkStopComplete(nextStopIdx)}
                    disabled={stopActionInProgress === nextStopIdx}
                    className="px-3.5 py-2.5 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.9rem]"
                    style={{ flex: 2 }}
                  >
                    {stopActionInProgress === nextStopIdx ? '...' : '✓ Mark Stop Complete'}
                  </button>
                  <button
                    onClick={() => { setUnreachableStopIdx(nextStopIdx); setUnreachableReason(''); }}
                    className="flex-1 px-3.5 py-2.5 rounded-[10px] bg-white cursor-pointer font-semibold text-[0.85rem]"
                    style={{ border: '1.5px solid #ff6b6b', color: '#ff6b6b' }}
                  >Unreachable</button>
                </div>
              </div>
            )}

            {/* Full list */}
            {showAllStops && (
              <div className="grid gap-2">
                {stops.map((s, i) => {
                  const statusBadge = {
                    pending: { bg: '#f1f5f9', color: '#64748b', label: 'Pending' },
                    completed: { bg: '#dcfce7', color: '#166534', label: '✓ Done' },
                    unreachable: { bg: '#fee2e2', color: '#991b1b', label: 'Unreachable' },
                    skipped: { bg: '#fef3c7', color: '#854d0e', label: 'Skipped' },
                  }[s.status] || { bg: '#f1f5f9', color: '#64748b', label: s.status };
                  return (
                    <div key={i} className="p-2.5 rounded-lg border border-slate-200 flex justify-between items-center"
                      style={{ background: i === nextStopIdx ? '#f0fdf4' : '#fff' }}>
                      <div className="text-[0.85rem]">
                        <strong>Stop {i + 1}:</strong> {s.type === 'pickup' ? `🧍 ${s.coRiderName}` : '👋 Drop-off'}
                        <div className="text-[0.78rem] text-muted">{s.address}</div>
                      </div>
                      <span className="px-2 py-[3px] rounded-full text-[0.7rem] font-bold"
                        style={{ background: statusBadge.bg, color: statusBadge.color }}>{statusBadge.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Stop unreachable modal (Batch 11) */}
      {unreachableStopIdx !== null && (
        <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <h3 className="font-serif mt-0 mb-3" style={{ color: '#dc2626' }}>
            Stop {unreachableStopIdx + 1} unreachable?
          </h3>
          <div className="form-group">
            <label className="form-label">Reason *</label>
            <select
              className="form-input form-select"
              value={unreachableReason}
              onChange={(e) => setUnreachableReason(e.target.value)}
            >
              <option value="">Select a reason...</option>
              <option value="wrong_address">Wrong address</option>
              <option value="road_closed">Road closed</option>
              <option value="access_denied">Access denied</option>
              <option value="co_rider_no_show">Passenger no-show (waited 5+ min)</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setUnreachableStopIdx(null); setUnreachableReason(''); }} className="flex-1 px-5 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Cancel</button>
            <button
              onClick={handleMarkStopUnreachable}
              disabled={!unreachableReason || stopActionInProgress === unreachableStopIdx}
              className="flex-1 px-5 py-3 rounded-xl border-0 text-white cursor-pointer font-bold"
              style={{ background: '#ff6b6b' }}
            >
              {stopActionInProgress === unreachableStopIdx ? '...' : 'Confirm Unreachable'}
            </button>
          </div>
        </div>
      )}

      {/* Geofence Status */}
      {isEnRouteOrLater && booking.status !== 'in_progress' && (
        <div className="mb-4 flex justify-center">
          <GeofenceIndicator isInside={isInsideGeofence} distance={distanceToPickup} hasArrived={hasArrived} />
        </div>
      )}

      {/* Waiting Timer */}
      {hasArrived && ['arrived'].includes(booking.status) && (
        <div className="mb-4">
          <WaitingTimerDisplay totalSeconds={totalSeconds} isRunning={isRunning} isPaused={isPaused}
            isInFreeWindow={isInFreeWindow} waitingFee={waitingFee} freeMinutes={5} />
        </div>
      )}

      {/* PIN Entry — geofence-gated */}
      {booking.status === 'arrived' && (
        <div className="mb-4">
          <TripPinEntry onSubmit={handlePinSubmit} disabled={!isInsideGeofence} locked={pinLocked}
            attemptsRemaining={pinAttemptsRemaining} lockoutMinutes={pinLockoutMinutes} />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 mb-6">
        {booking.status === 'confirmed' && (
          <>
            <button onClick={handleStartEnRoute} className="btn btn-primary">Start Driving to Pickup</button>
            <button onClick={() => setShowCancelModal(true)} className="p-3.5 rounded-full bg-transparent cursor-pointer font-semibold" style={{ border: '1.5px solid #ff6b6b', color: '#ff6b6b' }}>Cancel Booking</button>
          </>
        )}
        {booking.status === 'in_progress' && (
          <button onClick={() => handleCompleteTrip()} className="btn btn-primary">Complete Trip</button>
        )}
        {booking.status === 'arrived' && totalSeconds >= 900 && (
          <button onClick={handleNoShow} className="p-3.5 rounded-full bg-transparent cursor-pointer font-semibold" style={{ border: '1.5px solid #ff6b6b', color: '#ff6b6b' }}>Mark Rider No-Show (15min waited)</button>
        )}
      </div>

      {/* Driver Cancel Modal */}
      {showCancelModal && (() => {
        const pickupTime = booking.scheduledPickupTime?.toDate ? booking.scheduledPickupTime.toDate() : null;
        const hoursUntil = pickupTime ? (pickupTime - Date.now()) / 3600000 : 0;
        let estimatedPenalty = 0;
        if (hoursUntil > 0 && hoursUntil <= 0.5) estimatedPenalty = 25;
        else if (hoursUntil > 0 && hoursUntil <= 2) estimatedPenalty = 10;

        return (
          <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <h3 className="font-serif mt-0 mb-2" style={{ color: '#ff6b6b' }}>Cancel This Booking</h3>
            <div className="p-3 rounded-[10px] mb-3" style={{ background: estimatedPenalty > 0 ? '#fef2f2' : '#f0fdf4', border: `1px solid ${estimatedPenalty > 0 ? '#fecaca' : '#bbf7d0'}` }}>
              {estimatedPenalty > 0 ? (
                <>
                  <div className="font-semibold mb-1" style={{ color: '#dc2626' }}>Penalty: ${estimatedPenalty}</div>
                  <div className="text-[0.8rem] text-body">
                    Cancelling within {hoursUntil <= 0.5 ? '30 minutes' : '2 hours'} of pickup applies a ${estimatedPenalty} penalty. The booking returns to the pool for other drivers.
                  </div>
                </>
              ) : (
                <>
                  <div className="font-semibold mb-1" style={{ color: '#059669' }}>No penalty</div>
                  <div className="text-[0.8rem] text-body">
                    More than 2 hours before pickup. Booking returns to the pool — another driver may pick it up.
                  </div>
                </>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Reason *</label>
              <select className="form-input form-select" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}>
                <option value="">Select a reason...</option>
                <option value="vehicle_issue">Vehicle issue</option>
                <option value="cant_make_pickup">Can't make pickup time</option>
                <option value="emergency">Emergency</option>
                <option value="too_far">Pickup too far</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setShowCancelModal(false); setCancelReason(''); }} className="flex-1 px-5 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Keep Booking</button>
              <button onClick={handleDriverCancel} disabled={cancelling || !cancelReason} className="flex-1 px-5 py-3 rounded-xl border-0 text-white cursor-pointer font-bold" style={{ background: '#ff6b6b' }}>{cancelling ? 'Cancelling...' : `Confirm Cancel${estimatedPenalty > 0 ? ` ($${estimatedPenalty})` : ''}`}</button>
            </div>
          </div>
        );
      })()}


      {/* Chat */}
      <button onClick={() => setShowChat(!showChat)} className="w-full p-3.5 rounded-xl border border-slate-200 bg-white cursor-pointer font-semibold mb-4">{showChat ? 'Hide Chat' : 'Message Rider'}</button>

      {showChat && (
        <div className="rounded-2xl border border-slate-200 overflow-hidden mb-4">
          <ChatWindow bookingId={bookingId} userId={userProfile.id} userRole="driver" otherPartyName={booking.riderName || 'Rider'} booking={booking} />
        </div>
      )}


      {/* Fare */}
      <div className="p-4 rounded-xl bg-slate-50 text-center">
        <span className="text-body">Fare: </span>
        <span className="font-extrabold text-[1.2rem]">${booking.fareBreakdown?.totalFare?.toFixed(2) || '—'}</span>
        {waitingFee > 0 && <span style={{ color: '#ff6b6b' }}> + ${waitingFee.toFixed(2)} waiting</span>}
      </div>

      {/* SOS */}
      {isEnRouteOrLater && <SOSButton onActivate={handleSOS} />}

      {/* Report Incident (Batch 10) — available throughout the trip */}
      <button onClick={() => navigate('/driver/report-incident', { state: {
        bookingId, bookingReferenceNumber: booking.referenceNumber, driverId: booking.driverId, riderId: booking.riderId,
      }})} className="w-full p-3 rounded-xl bg-white cursor-pointer font-semibold text-[0.9rem] mt-3"
        style={{ border: '1.5px solid #dc2626', color: '#dc2626' }}>📋 Report incident or notifiable occurrence</button>

      {/* Route deviation reason (shown when actual distance >> estimate) */}
      {showDeviationModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="bg-white rounded-2xl p-5" style={{ maxWidth: 420, width: '100%' }}>
            <h3 className="font-serif mt-0 mb-2">This trip went further than planned</h3>
            <p className="text-body text-[0.85rem] mb-3">
              The actual distance is longer than the booked route. If a road condition forced a
              detour, tell us why — genuine cases are reviewed and may be compensated. The rider is
              only charged the original quote.
            </p>
            <select className="form-input form-select mb-3" value={deviationReason} onChange={(e) => setDeviationReason(e.target.value)}>
              <option value="">Select a reason…</option>
              <option value="roadworks">Roadworks / construction</option>
              <option value="closure">Road closed</option>
              <option value="diversion">Accident / police diversion</option>
              <option value="traffic">Severe traffic / unexpected delay</option>
              <option value="rider_request">Rider asked to change route</option>
              <option value="other">Other</option>
            </select>
            <div className="flex gap-2">
              <button onClick={() => { setShowDeviationModal(false); handleCompleteTrip('none_given'); }}
                className="flex-1 p-3 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">No special reason</button>
              <button onClick={() => { if (!deviationReason) { toast.error('Pick a reason or choose “No special reason”'); return; } setShowDeviationModal(false); handleCompleteTrip(deviationReason); }}
                className="flex-1 p-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">Submit & complete</button>
            </div>
          </div>
        </div>
      )}
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}